import React, { useEffect, useState } from 'react';
import { getTheme, useThemeMode } from '../theme';
import type { QidUser } from '../api';
import { bearerHeaders } from '../api';
import { apiFetch } from '../appFetch';
import { ThemeSwitch } from '../components/ThemeSwitch';
import { McpConnectionsCard } from '../components/McpConnections';
import { ProfileIntegrationsPanel, BackgroundTrackLogSection } from '../panels';
import { PushSettingsSection } from '../panels/PushSettingsSection';
import { HuntMediaSettingsSection } from '../panels/HuntMediaSettingsSection';
import { BlockedUsersSection, SupportSection } from '../panels/SafetySupportSection';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { isMetaGlassesAvailable } from '../metaGlasses';

declare const __APP_BUILD_ID__: string;

/** Om appen: version, byggnummer och variant (Vanlig/Glasögon), så att man ser vilket TestFlight-bygge som körs (MG1b). */
export function AboutSection({ cardStyle }: { cardStyle: React.CSSProperties }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const native = Capacitor.isNativePlatform();
  const [info, setInfo] = useState<{ version: string; build: string } | null>(null);
  useEffect(() => {
    if (!native) return;
    CapApp.getInfo().then(i => setInfo({ version: i.version, build: i.build })).catch(() => {});
  }, [native]);
  const web = typeof __APP_BUILD_ID__ !== 'undefined' ? __APP_BUILD_ID__ : 'dev';
  const rows: [string, string][] = native
    ? [['Version', info?.version ?? '…'], ['Bygge', info?.build ?? '…'], ['Variant', isMetaGlassesAvailable() ? 'Glasögon' : 'Vanlig'], ['Webb', web]]
    : [['Webb', web]];
  return (
    <div style={{ ...cardStyle, marginTop: 16 }} data-testid="about">
      <div style={{ color: T.text, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Om Qvenari</div>
      {rows.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '3px 0' }}>
          <span style={{ color: T.textSub }}>{k}</span><span style={{ color: T.text, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Inställningar: allt som inte är jag som person — utseende, notiser, integrationer (Telegram, WhatsApp,
 * Garmin, PAJ GPS m.fl.), kopplade AI-tjänster, blockerade användare och support. Profilen (VProfil)
 * har kvar uppgifter, licenser, hundar, vapen, konto och radering.
 */
export function VInstallningar({ user, onOpenProfile }: { user: QidUser | null; onOpenProfile?: () => void }) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const cS: React.CSSProperties = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 };
  const token = localStorage.getItem('qvenari_access_token');
  // Hundarna behövs för att koppla Garmin-halsband till rätt hund i integrationspanelen.
  const [dogs, setDogs] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    if (!token) return;
    apiFetch('/api/profile', { headers: { ...bearerHeaders(token) } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.dogs) setDogs(d.dogs); })
      .catch(() => {});
  }, [token]);

  return (
    <div className="view-pad" style={{ paddingTop: 160, paddingBottom: 24, paddingLeft: 'clamp(12px,3vw,28px)', paddingRight: 'clamp(12px,3vw,28px)', maxWidth: 760, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 20 }}>
        <h1 style={{ color: T.text, fontSize: 20, fontWeight: 700, margin: 0 }}>Inställningar</h1>
        {onOpenProfile && user && (
          <button type="button" onClick={onOpenProfile} style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 999, padding: '8px 14px', color: T.text, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Min profil</button>
        )}
      </div>

      <div style={{ ...cS, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: T.text, fontSize: 14, fontWeight: 600 }}>Utseende</div>
          <div style={{ color: T.textSub, fontSize: 12, marginTop: 2 }}>{mode === 'dark' ? 'Mörkt läge — lättare för ögonen i svagt ljus' : 'Ljust läge'}</div>
        </div>
        <ThemeSwitch color={T.text} />
      </div>

      {user && <PushSettingsSection cardStyle={cS} />}

      {user && <HuntMediaSettingsSection cardStyle={cS} />}

      {user && token && <>
        <ProfileIntegrationsPanel userId={user.email || ''} token={token} dogs={dogs} qSub={user.sub} />
        <BackgroundTrackLogSection />
      </>}

      {user && <McpConnectionsCard cardStyle={cS} />}

      {user && <BlockedUsersSection cardStyle={cS} />}

      <SupportSection cardStyle={cS} email={user?.email} name={user?.name} />

      <AboutSection cardStyle={cS} />

      <div style={{ marginTop: 16, fontSize: 13, color: T.textSub }}><a href="/integritet" style={{ color: T.amber }}>Integritetspolicy</a> · <a href="/villkor" style={{ color: T.amber }}>Användarvillkor</a></div>
    </div>
  );
}
